/**
 * Small GLSL source-rewriting utilities.
 *
 * Everything here is comment- and string-aware, and every rewrite is
 * **newline-preserving**: a replacement always carries the same number of line
 * breaks as the text it replaced. That is what keeps compile-error line numbers
 * pointing at the line the author actually wrote, which matters because those
 * numbers are surfaced in the editor and fed back to the AI for repair.
 *
 * Naive regexes are not good enough for either job: `\b` matching would rewrite
 * identifiers inside comments, and `\(([^,]+),([^)]+)\)` mangles nested calls
 * like `IMG_PIXEL(img, vec2(x, mix(a, b, t)))`.
 */

const IDENT_START = /[A-Za-z_]/;
const IDENT_PART = /[A-Za-z0-9_]/;

type ScanState = 'code' | 'line-comment' | 'block-comment' | 'string';

/** Count the line breaks in a string. */
function newlineCount(text: string): number {
  let n = 0;
  for (let i = 0; i < text.length; i++) if (text[i] === '\n') n++;
  return n;
}

/**
 * Pad `replacement` with trailing newlines so it occupies the same number of
 * lines as `original`, keeping every subsequent line number unchanged.
 */
function preserveLines(original: string, replacement: string): string {
  const deficit = newlineCount(original) - newlineCount(replacement);
  return deficit > 0 ? replacement + '\n'.repeat(deficit) : replacement;
}

/**
 * Walk `source`, invoking `onIdentifier` for every identifier that appears in
 * real code (never inside a comment or string). Returning a string replaces the
 * identifier; returning undefined leaves it alone.
 */
export function mapIdentifiers(
  source: string,
  onIdentifier: (name: string, index: number) => string | undefined,
): string {
  let out = '';
  let state: ScanState = 'code';
  let i = 0;

  while (i < source.length) {
    const c = source[i];
    const next = source[i + 1];

    if (state === 'line-comment') {
      out += c;
      if (c === '\n') state = 'code';
      i++;
      continue;
    }

    if (state === 'block-comment') {
      if (c === '*' && next === '/') {
        out += '*/';
        state = 'code';
        i += 2;
      } else {
        out += c;
        i++;
      }
      continue;
    }

    if (state === 'string') {
      out += c;
      if (c === '\\' && next !== undefined) {
        out += next;
        i += 2;
        continue;
      }
      if (c === '"') state = 'code';
      i++;
      continue;
    }

    if (c === '/' && next === '/') {
      out += '//';
      state = 'line-comment';
      i += 2;
      continue;
    }

    if (c === '/' && next === '*') {
      out += '/*';
      state = 'block-comment';
      i += 2;
      continue;
    }

    if (c === '"') {
      out += c;
      state = 'string';
      i++;
      continue;
    }

    if (IDENT_START.test(c)) {
      let j = i + 1;
      while (j < source.length && IDENT_PART.test(source[j])) j++;
      const name = source.slice(i, j);
      const replacement = onIdentifier(name, i);
      out += replacement ?? name;
      i = j;
      continue;
    }

    out += c;
    i++;
  }

  return out;
}

/** Replace whole identifiers using a lookup table. Comments are left alone. */
export function replaceIdentifiers(source: string, map: Record<string, string>): string {
  return mapIdentifiers(source, (name) =>
    Object.prototype.hasOwnProperty.call(map, name) ? map[name] : undefined,
  );
}

/** True when `source` mentions `name` as a real identifier, not in a comment. */
export function usesIdentifier(source: string, name: string): boolean {
  let found = false;
  mapIdentifiers(source, (ident) => {
    if (ident === name) found = true;
    return undefined;
  });
  return found;
}

/** Every distinct identifier appearing in real code. */
export function collectIdentifiers(source: string): Set<string> {
  const names = new Set<string>();
  mapIdentifiers(source, (name) => {
    names.add(name);
    return undefined;
  });
  return names;
}

interface CallSite {
  /** Index of the first character of the function name. */
  start: number;
  /** Index one past the closing parenthesis. */
  end: number;
  /** Argument source text, split at parenthesis depth 0 and trimmed. */
  args: string[];
}

/**
 * Find calls to `name`, splitting arguments at depth 0 only so nested calls and
 * vector constructors survive. Skips comments and strings.
 */
export function findCalls(source: string, name: string): CallSite[] {
  const sites: CallSite[] = [];

  mapIdentifiers(source, (ident, index) => {
    if (ident !== name) return undefined;

    let k = index + ident.length;
    while (k < source.length && /\s/.test(source[k])) k++;
    if (source[k] !== '(') return undefined;

    const args: string[] = [];
    let depth = 0;
    let argStart = k + 1;
    let inString = false;

    for (let p = k; p < source.length; p++) {
      const ch = source[p];

      if (inString) {
        if (ch === '\\') p++;
        else if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') { inString = true; continue; }

      if (ch === '(' || ch === '[') {
        depth++;
        continue;
      }
      if (ch === ']') {
        depth--;
        continue;
      }
      if (ch === ')') {
        depth--;
        if (depth === 0) {
          const tail = source.slice(argStart, p).trim();
          if (tail !== '' || args.length > 0) args.push(tail);
          sites.push({ start: index, end: p + 1, args });
          return undefined;
        }
        continue;
      }
      if (ch === ',' && depth === 1) {
        args.push(source.slice(argStart, p).trim());
        argStart = p + 1;
      }
    }

    return undefined;
  });

  return sites;
}

/**
 * Rewrite every call to `name`. `build` receives the parsed arguments and
 * returns replacement source, or null to leave the call untouched.
 *
 * Replacements are line-count preserving, so error line numbers stay exact.
 */
export function rewriteCalls(
  source: string,
  name: string,
  build: (args: string[]) => string | null,
): string {
  const sites = findCalls(source, name);
  if (sites.length === 0) return source;

  let out = '';
  let cursor = 0;

  for (const site of sites) {
    const original = source.slice(site.start, site.end);
    const replacement = build(site.args);
    out += source.slice(cursor, site.start);
    out += replacement === null ? original : preserveLines(original, replacement);
    cursor = site.end;
  }

  out += source.slice(cursor);
  return out;
}
